import { Box, Typography } from "@mui/material";
import { useChatTranscript } from "../../../hooks/useInteraction";
import chatDisplayStyles from "./ChatDisplayStyles";
import { ChatMessage } from "../../../types/chat/chat";
import dayjs from "dayjs";

interface ChatDisplayProps {
  customerId: string | undefined;
  interactionId: string | undefined;
}

const ChatDisplay = ({
  customerId,
  interactionId,
}: Readonly<ChatDisplayProps>) => {
  const {
    data: transcript,
    isLoading,
    isError,
    error,
  } = useChatTranscript(customerId, interactionId);

  if (isLoading) return <div>Loading transcript...</div>;
  if (isError) {
    console.error(`Error fetching transcript: ${error.message}`);
    return (
      <div>Error fetching transcript. Please try again or contact support.</div>
    );
  }

  return (
    <Box sx={chatDisplayStyles.container}>
      {transcript?.messages.map((message) => {
        const isAgent = message.participant?.type === "AGENT";
        const alignment = isAgent ? "right" : "left";

        return (
          <Box key={message.timestamp} sx={chatDisplayStyles.messageContainer}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              align={alignment}
            >
              {participantName(message)}{" "}
            </Typography>
            <Box sx={chatDisplayStyles.messageContent(isAgent)}>
              <Message message={message} alignment={alignment} />
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

function Message({
  message,
  alignment,
}: Readonly<{
  message: ChatMessage;
  alignment: "left" | "right";
}>) {
  if (message.participant?.type === "SYSTEM") {
    return (
      <>
        <Typography variant="body2" color="text.secondary" align={alignment}>
          {message.text}
        </Typography>
        <Timestamp message={message} alignment={alignment} />
      </>
    );
  }
  if (message.messageType !== "MESSAGE") {
    return (
      <>
        <Typography variant="body2" color="text.secondary" align={alignment}>
          {eventMessage(message)}
        </Typography>
        <Timestamp message={message} alignment={alignment} />
      </>
    );
  }
  return (
    <>
      <Typography variant="body1" color="text.primary" align={alignment}>
        {message.text}
      </Typography>
      <Timestamp message={message} alignment={alignment} />
    </>
  );
}

function Timestamp({
  message,
  alignment,
}: Readonly<{
  message: ChatMessage;
  alignment: "left" | "right";
}>) {
  return (
    <Typography variant="caption" color="text.secondary" align={alignment}>
      {message.timestamp
        ? dayjs(message.timestamp).toDate().toLocaleString()
        : ""}
    </Typography>
  );
}

function eventMessage(message: ChatMessage): string {
  switch (message.messageType) {
    case "PARTICIPANT_JOINED":
      return `${message.participant?.name} has joined the chat`;
    case "PARTICIPANT_LEFT":
      return `${message.participant?.name} has left the chat`;
    default:
      console.warn(
        `Unknown message type: ${message.messageType}. Returning empty string.`
      );
      return "";
  }
}

function participantName(message: ChatMessage): string {
  if (["Customer", "CUSTOMER"].includes(message.participant?.name ?? "")) {
    return "Customer";
  }
  return `${message.participant?.name} (${message.participant?.type})`;
}

export default ChatDisplay;
